// @flow
import React, { useMemo, useRef } from 'react';
import type { Node } from 'react';
import {
  Stack,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { AsyncBox } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import { Modal } from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import { Table } from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import useIntl from '../../hooks/useIntl';
import useBaseTableProps from '../../hooks/useBaseTableProps';
import { formatCalendarDateUtc } from '../../utils/date-helpers';
import { useGlDetail } from './hooks';
import { formatMoney } from './utils';
import messages from './messages';
import type { DrillDown } from './types';

const NAME = 'variance-report-gl-detail';

const GlDetailModal = ({
  month,
  drillDown,
  onClose,
}: {
  month: string,
  drillDown: ?DrillDown,
  onClose: () => void,
}): Node => {
  const intl = useIntl();
  const baseTableProps = useBaseTableProps();
  // Keep showing the last drill-down while the modal animates closed, instead of a blank modal.
  const lastDrillDown = useRef(drillDown);
  if (drillDown) {
    lastDrillDown.current = drillDown;
  }
  const shown = drillDown ?? lastDrillDown.current;
  const { isLoading, error, detail } = useGlDetail(month, shown);

  const headers = useMemo(
    () =>
      [
        ['date', messages.glDate],
        ['description', messages.glDescription],
        ['reference', messages.glReference],
        ['party', messages.glParty],
        ['debit', messages.glDebit],
        ['credit', messages.glCredit],
      ].map(([id, message]) => ({ id, label: intl.formatMessage(message) })),
    [intl],
  );

  const rows = useMemo(() => {
    if (!detail) {
      return [];
    }
    const lines = detail.lines.map((line, index) => ({
      id: `line-${index}`,
      date: { value: formatCalendarDateUtc(line.date) ?? line.date },
      description: { value: line.description },
      reference: { value: line.reference },
      party: { value: line.party },
      debit: { value: line.debit ? formatMoney(intl, line.debit) : '' },
      credit: { value: line.credit ? formatMoney(intl, line.credit) : '' },
    }));
    return [
      ...lines,
      {
        id: 'total',
        date: {
          value: (
            <Typography variant="labelMedium">
              {intl.formatMessage(messages.glTotal)}
            </Typography>
          ),
        },
        debit: {
          value: (
            <Typography variant="labelMedium">
              {formatMoney(intl, detail.totalDebit)}
            </Typography>
          ),
        },
        credit: {
          value: (
            <Typography variant="labelMedium">
              {formatMoney(intl, detail.totalCredit)}
            </Typography>
          ),
        },
      },
    ];
  }, [detail, intl]);

  const title = shown
    ? intl.formatMessage(messages.glDetailTitle, {
        label: shown.label,
        range: intl.formatMessage(
          shown.range === 'ytd' ? messages.rangeYtd : messages.rangeMonth,
        ),
      })
    : '';

  let content = null;
  if (error) {
    content = (
      <Typography
        role="alert"
        variant="bodyMedium"
        sx={{ color: 'error.main' }}
      >
        {error}
      </Typography>
    );
  } else if (detail && detail.lines.length === 0) {
    content = (
      <Typography variant="bodyMedium" sx={{ color: 'text.secondary' }}>
        {intl.formatMessage(messages.glDetailEmpty)}
      </Typography>
    );
  } else if (detail) {
    content = (
      <Stack spacing={1}>
        {detail.truncated && (
          <Typography variant="bodySmall" sx={{ color: 'text.secondary' }}>
            {intl.formatMessage(messages.glDetailTruncated, {
              count: detail.lines.length,
            })}
          </Typography>
        )}
        <Table
          {...baseTableProps}
          headers={headers}
          intl={intl}
          name={NAME}
          rows={rows}
          count={detail.lines.length}
          totalCount={detail.lines.length}
          showSettings={false}
        />
      </Stack>
    );
  }

  return (
    <Modal
      open={Boolean(drillDown)}
      onClose={onClose}
      title={title}
      ModalProps={{
        PaperProps: {
          sx: { width: { xs: 'calc(100% - 32px)', md: 960 } },
        },
      }}
    >
      <AsyncBox loading={isLoading}>{content}</AsyncBox>
    </Modal>
  );
};

export default GlDetailModal;
